import { isNullOrUndefined } from '@syncfusion/ej2-base';
import { formatUnit, removeClass } from '@syncfusion/ej2-base';
import { colGroupRefresh, columnWidthChanged, scroll, columnVisibilityChanged, refreshFooterRenderer } from '../base/constant';
import { ContentRender } from './content-renderer';
import { RowRenderer } from './row-renderer';
import { SummaryModelGenerator } from '../services/summary-model-generator';
import { calculateAggregate, iterateExtend, addRemoveEventListener, applyStickyLeftRightPosition } from '../base/util';
import { DataUtil } from '@syncfusion/ej2-data';
import * as literals from '../base/string-literals';
/**
 * Footer module is used to render grid content
 *
 * @hidden
 */
export class FooterRenderer extends ContentRender {
    constructor(gridModule, serviceLocator) {
        super(gridModule, serviceLocator);
        this.aggregates = {};
        this.parent = gridModule;
        this.locator = serviceLocator;
        this.modelGenerator = new SummaryModelGenerator(this.parent);
        this.addEventListener();
    }
    /**
     * The function is used to render grid footer div
     *
     * @returns {void}
     */
    renderPanel() {
        const div = this.parent.createElement('div', { className: literals.gridFooter });
        const innerDiv = this.parent.createElement('div', { className: 'e-summarycontent' });
        div.appendChild(innerDiv);
        this.setPanel(div);
        if (this.parent.getPager() != null) {
            this.parent.element.insertBefore(div, this.parent.getPager());
        }
        else {
            this.parent.element.appendChild(div);
        }
    }
    /**
     * The function is used to render grid footer table
     *
     * @returns {void}
     */
    renderTable() {
        const innerDiv = this.createContentTable('_footer_table');
        const table = innerDiv.querySelector('.' + literals.table);
        const tFoot = this.parent.createElement('tfoot');
        table.appendChild(tFoot);
        this.setTable(table);
    }
    renderSummaryContent(e, table, cStart, cEnd) {
        const input = this.parent.dataSource instanceof Array ? !this.parent.getDataModule().isRemote() &&
            this.parent.parentDetails ? this.getData() : this.parent.dataSource : this.parent.currentViewData;
        const summaries = this.modelGenerator.getData();
        const dummies = isNullOrUndefined(cStart) ? this.modelGenerator.getColumns() :
            this.modelGenerator.getColumns(cStart);
        // eslint-disable-next-line max-len
        const rows = isNullOrUndefined(cStart) ? this.modelGenerator.generateRows(input, e || this.aggregates) :
            this.modelGenerator.generateRows(input, e || this.aggregates, cStart, cEnd);
        const fragment = document.createDocumentFragment();
        const rowrenderer = new RowRenderer(this.locator, null, this.parent);
        rowrenderer.element = this.parent.createElement('TR', { className: 'e-summaryrow' });
        for (let srow = 0, len = summaries.length; srow < len; srow++) {
            const row = rows[parseInt(srow.toString(), 10)];
            if (!row) {
                continue;
            }
            const tr = rowrenderer.render(row, dummies);
            if (tr.querySelectorAll('.e-leftfreeze').length && tr.querySelectorAll('.e-indentcell').length) {
                const td = tr.querySelectorAll('.e-indentcell');
                for (let i = 0; i < td.length; i++) {
                    td[parseInt(i.toString(), 10)].classList.add('e-leftfreeze');
                    applyStickyLeftRightPosition(td[parseInt(i.toString(), 10)], i * 30, this.parent.enableRtl, 'Left');
                }
            }
            if (this.parent.isFrozenGrid() && tr.querySelectorAll('.e-summarycell').length) {
                removeClass([].slice.call(tr.querySelectorAll('.e-summarycell')), ['e-freezeleftborder', 'e-freezerightborder']);
            }
            fragment.appendChild(tr);
        }
        table.tFoot.appendChild(fragment);
        this.aggregates = !isNullOrUndefined(e) ? e : this.aggregates;
    }
    refresh(e) {
        this.getTable().tFoot.innerHTML = '';
        this.renderSummaryContent(e, this.getTable(), undefined, undefined);
        if (isNullOrUndefined(e) && this.parent.isAutoFitColumns) {
            this.parent.autoFitColumns();
        }
        this.onScroll();
    }
    refreshCol() {
        // frozen table
        const mheaderCol = this.parent.element.querySelector('.' + literals.gridHeader).querySelector(literals.colGroup).cloneNode(true);
        this.getTable().replaceChild(mheaderCol, this.getColGroup());
        this.setColGroup(mheaderCol);
    }
    onWidthChange(args) {
        this.getColFromIndex(args.index).style.width = formatUnit(args.width);
        if (this.parent.allowResizing && args.module === 'resize') {
            this.updateFooterTableWidth(this.getTable());
        }
    }
    onScroll(e = {
        left: this.parent.getContent().firstChild.scrollLeft
    }) {
        this.getTable().parentElement.scrollLeft = e.left;
    }
    getColFromIndex(index) {
        return this.getColGroup().children[parseInt(index.toString(), 10)];
    }
    columnVisibilityChanged() {
        this.refresh();
    }
    addEventListener() {
        this.evtHandlers = [{ event: colGroupRefresh, handler: this.refreshCol },
            { event: columnWidthChanged, handler: this.onWidthChange },
            { event: scroll, handler: this.onScroll },
            { event: columnVisibilityChanged, handler: this.columnVisibilityChanged },
            { event: refreshFooterRenderer, handler: this.refreshFooterRenderer }];
        addRemoveEventListener(this.parent, this.evtHandlers, true, this);
    }
    removeEventListener() {
        addRemoveEventListener(this.parent, this.evtHandlers, false);
    }
    updateFooterTableWidth(tFoot) {
        const tHead = this.parent.getHeaderTable();
        if (tHead && tFoot) {
            tFoot.style.width = tHead.style.width;
        }
    }
    refreshFooterRenderer(editedData) {
        const aggregates = this.onAggregates(editedData);
        this.refresh(aggregates);
    }
    getIndexByKey(data, ds) {
        const key = this.parent.getPrimaryKeyFieldNames()[0];
        for (let i = 0; i < ds.length; i++) {
            if (ds[parseInt(i.toString(), 10)][`${key}`] === data[`${key}`]) {
                return i;
            }
        }
        return -1;
    }
    getData() {
        return this.parent.getDataModule().dataManager.executeLocal(this.parent.getDataModule().generateQuery(true));
    }
    onAggregates(editedData) {
        editedData = editedData instanceof Array ? editedData : [];
        const field = this.parent.getPrimaryKeyFieldNames()[0];
        let dataSource = [];
        let isModified = false;
        let batchChanges = {};
        const gridData = 'dataSource';
        let isFiltered = false;
        if (!this.parent.renderModule.data.isRemote() && this.parent.allowFiltering && this.parent.filterSettings.columns.length) {
            isFiltered = true;
        }
        let currentViewData = this.parent.dataSource instanceof Array ?
            (isFiltered ? this.parent.getFilteredRecords() : this.parent.dataSource) : (this.parent.dataSource[`${gridData}`].json.length ?
            this.parent.dataSource[`${gridData}`].json : this.parent.getCurrentViewRecords());
        if (this.parent.parentDetails && !this.parent.getDataModule().isRemote()) {
            currentViewData = this.getData();
        }
        if (this.parent.editModule) {
            batchChanges = this.parent.editModule.getBatchChanges();
        }
        if (Object.keys(batchChanges).length) {
            for (let i = 0; i < currentViewData.length; i++) {
                isModified = false;
                // eslint-disable-next-line max-len
                if (batchChanges[literals.changedRecords].length && this.getIndexByKey(currentViewData[parseInt(i.toString(), 10)], batchChanges[literals.changedRecords]) > -1) {
                    isModified = true;
                    // eslint-disable-next-line max-len
                    dataSource.push(batchChanges[literals.changedRecords][this.getIndexByKey(currentViewData[parseInt(i.toString(), 10)], batchChanges[literals.changedRecords])]);
                }
                // eslint-disable-next-line max-len
                if (batchChanges[literals.deletedRecords].length && this.getIndexByKey(currentViewData[parseInt(i.toString(), 10)], batchChanges[literals.deletedRecords]) > -1) {
                    isModified = true;
                }
                else if (!isModified) {
                    dataSource.push(currentViewData[parseInt(i.toString(), 10)]);
                }
            }
            if (batchChanges[literals.addedRecords].length) {
                for (let i = 0; i < batchChanges[literals.addedRecords].length; i++) {
                    dataSource.push(batchChanges[literals.addedRecords][parseInt(i.toString(), 10)]);
                }
            }
        }
        else {
            if (editedData.length) {
                const data = iterateExtend(currentViewData);
                dataSource = data.map((item) => {
                    const idVal = DataUtil.getObject(field, item);
                    let value;
                    const hasVal = editedData.some((cItem) => {
                        value = cItem;
                        return idVal === DataUtil.getObject(field, cItem);
                    });
                    return hasVal ? value : item;
                });
            }
            else {
                dataSource = currentViewData;
            }
        }
        const eData = editedData;
        if ((eData.type && eData.type === 'cancel')) {
            dataSource = currentViewData;
        }
        const aggregate = {};
        let agrVal;
        const aggregateRows = this.parent.aggregates;
        for (let i = 0; i < aggregateRows.length; i++) {
            for (let j = 0; j < aggregateRows[parseInt(i.toString(), 10)].columns.length; j++) {
                let data = [];
                const type = aggregateRows[parseInt(i.toString(), 10)]
                    .columns[parseInt(j.toString(), 10)].type.toString();
                data = dataSource;
                agrVal = calculateAggregate(type, data, aggregateRows[parseInt(i.toString(), 10)]
                    .columns[parseInt(j.toString(), 10)], this.parent);
                aggregate[aggregateRows[parseInt(i.toString(), 10)].columns[parseInt(j.toString(), 10)].field + ' - ' + type.toLowerCase()] = agrVal;
            }
        }
        const result = {
            result: dataSource,
            count: dataSource.length,
            aggregates: aggregate
        };
        return result;
    }
}
